/**
 * Cart selectors. Everything derived is derived here, during render — never mirrored into state
 * by an effect, and never stored twice.
 */

import { createSelector } from '@reduxjs/toolkit';

import type { CartLine, CartState, GenericCartLine } from './cartSlice.js';

type WithCart = { readonly cart: CartState };

export const selectCart = (state: WithCart): CartState => state.cart;
export const selectLines = (state: WithCart): readonly CartLine[] => state.cart.lines;
export const selectGenericLines = (state: WithCart): readonly GenericCartLine[] =>
  state.cart.genericLines;
export const selectPriceType = (state: WithCart) => state.cart.priceType;
export const selectSubmission = (state: WithCart) => state.cart.submission;
export const selectCartError = (state: WithCart) => state.cart.lastError;
export const selectIdempotencyKey = (state: WithCart) => state.cart.idempotencyKey;

/** Non-null once the sale has been rung up and its stock has moved. */
export const selectSaleId = (state: WithCart) => state.cart.saleId;

/** Non-null only once the salesperson has picked a registered customer. Most sales have none. */
export const selectCustomerId = (state: WithCart) => state.cart.customerId;
export const selectCustomerName = (state: WithCart) => state.cart.customerName;
export const selectCustomerPhone = (state: WithCart) => state.cart.customerPhone;

/** Non-null only once a broker has been attached. Most sales have none. */
export const selectBrokerId = (state: WithCart) => state.cart.brokerId;
export const selectBrokerName = (state: WithCart) => state.cart.brokerName;

export const selectLineCount = createSelector(
  [selectLines, selectGenericLines],
  (lines, genericLines) =>
    lines.reduce((total, line) => total + line.quantity, 0) +
    genericLines.reduce((total, line) => total + line.quantity, 0),
);

export const selectTotalKes = createSelector(
  [selectLines, selectGenericLines],
  (lines, genericLines) =>
    lines.reduce((total, line) => total + line.unitPriceKes * line.quantity, 0) +
    genericLines.reduce((total, line) => total + line.unitPriceKes * line.quantity, 0),
);

/** How many distinct rows the cart has — catalogue lines and misc. lines both count as one each. */
export const selectRowCount = createSelector(
  [selectLines, selectGenericLines],
  (lines, genericLines) => lines.length + genericLines.length,
);

export const selectIsEmpty = createSelector(
  [selectLines, selectGenericLines],
  (lines, genericLines) => lines.length === 0 && genericLines.length === 0,
);

/**
 * Lines the salesperson has to look at before this sale can go through, with the reason. Two
 * cases, both real: a serialized line with no IMEI chosen, and a quantity beyond what the cached
 * catalogue says is on hand.
 *
 * Overselling is a warning, not a block — negative stock is permitted by design, and a till that
 * refuses a sale because a count is stale loses the sale rather than fixing the count.
 */
export type LineWarning = {
  readonly key: string;
  /** `blocking` stops the sale; `warning` is shown and sold through. */
  readonly severity: 'blocking' | 'warning';
  readonly message: string;
};

export const selectLineWarnings = createSelector([selectLines], (lines): readonly LineWarning[] =>
  lines.flatMap<LineWarning>((line) => {
    if (line.isSerialized && line.inventoryUnitId === null) {
      return [{ key: line.key, severity: 'blocking', message: 'Choose an IMEI for this handset.' }];
    }

    if (line.quantity > line.availableQuantity) {
      return [
        {
          key: line.key,
          severity: 'warning',
          message:
            line.availableQuantity <= 0
              ? 'The catalogue shows none in stock. Selling anyway will make the count negative.'
              : `Only ${line.availableQuantity} in stock. Selling ${line.quantity} will make the count negative.`,
        },
      ];
    }

    return [];
  }),
);

export const selectCanSubmit = createSelector(
  [selectLines, selectGenericLines, selectLineWarnings, selectSubmission],
  (lines, genericLines, warnings, submission) =>
    (lines.length > 0 || genericLines.length > 0) &&
    submission !== 'submitting' &&
    !warnings.some((warning) => warning.severity === 'blocking'),
);
